/**
 * The catalogue keys for the contract enums that are rendered as labels.
 *
 * One map per enum, keyed by the contract's own value union, so a value added to
 * an enum without a label here is a compile error rather than a screen showing a
 * raw wire value like `field_force_leadership`. Kept in `shared` because more than
 * one screen renders these vocabularies - the calendar filter bar and the
 * Initiative list both do - and two copies of the same map would drift.
 */
import type { BottleneckState } from '@complan/domain';
import type {
  FIELD_FORCE_ROLES,
  INITIATIVE_STATUSES,
  ReadinessStateValue,
  UtilizationPoint,
} from '@complan/contracts';

/** R9.1's Initiative statuses. */
export const STATUS_LABEL_KEYS: Record<(typeof INITIATIVE_STATUSES)[number], string> = {
  draft: 'status.draft',
  submitted: 'status.submitted',
  planned: 'status.planned',
  approved: 'status.approved',
  final: 'status.final',
  cancelled: 'status.cancelled',
};

/** The Field Force roles: role level only, never an employee (R18.13). */
export const FF_ROLE_LABEL_KEYS: Record<(typeof FIELD_FORCE_ROLES)[number], string> = {
  ade: 'fieldForceRole.ade',
  bde: 'fieldForceRole.bde',
};

/**
 * The three series of R5.2's utilization visual.
 *
 * Keyed by the utilization point's own union rather than by `FIELD_FORCE_ROLES`, because
 * `combined` is a third *series* and not a third role: it is the two roles read together.
 * {@link FF_ROLE_LABEL_KEYS} therefore does not carry it, and should not - a role filter
 * offering "Combined" would be offering a role nobody holds.
 */
export const UTILIZATION_ROLE_LABEL_KEYS: Record<UtilizationPoint['ffRole'], string> = {
  ade: 'fieldForceRole.ade',
  bde: 'fieldForceRole.bde',
  combined: 'fieldForceRole.combined',
};

/** R8.3's five Milestone_Readiness states, as the R5.4 funnel renders them. */
export const READINESS_STATE_LABEL_KEYS: Record<ReadinessStateValue, string> = {
  completed: 'readinessState.completed',
  planned: 'readinessState.planned',
  due: 'readinessState.due',
  overdue: 'readinessState.overdue',
  missing: 'readinessState.missing',
};

/** R6.4's four Bottleneck_States, as rendered by {@link CapacityStateBadge}. */
export const BOTTLENECK_STATE_LABEL_KEYS: Record<BottleneckState, string> = {
  normal: 'manpower.bottleneckState.normal',
  warning: 'manpower.bottleneckState.warning',
  over_capacity: 'manpower.bottleneckState.overCapacity',
  data_incomplete: 'manpower.bottleneckState.dataIncomplete',
};
